import { useId, useMemo, useState } from "react";
import { useFormatters, useTranslation } from "@ferrivia/i18n";
import { Text } from "@ferrivia/ui";
import { useTracker } from "@ferrivia/auth-web";
import { ChevronDown } from "lucide-react";
import { useAnswerText, useQuestionnaireCopy } from "@ferrivia/questionnaire-ui";
import {
  answerableGroupFields,
  INSTRUMENT_DEFINITIONS,
  parsePhotoIds,
  reachableQuestions,
  sectionProgress,
  type AnswerMap,
  type AnswerValue,
  type ItemFieldMap,
  type ItemMap,
  type QuestionDefinition,
  type QuestionnaireDetail,
  type QuestionnaireStatus,
  type RepeatingQuestionDefinition,
  type SectionProgress,
  type SingleQuestionDefinition,
} from "@ferrivia/api-contract";
import { useCustomerQuestionnaire } from "../../../../hooks/useCustomerQuestionnaire";
import { QuestionnairePhotoViewer } from "./QuestionnairePhotoViewer";
import s from "./ScreenerPane.module.css";

export interface ScreenerPaneProps {
  customerId: string;
  /** Threaded from CustomerWorkspace's own outlet-context staff user, same
      as ChatFeedPane's staffUserId -- clinic.patient_questionnaire_section_opened
      is browser-reported and carries who opened the section, unlike the
      server-emitted clinic.patient_questionnaire_viewed. */
  staffUserId?: string;
}

function toAnswerMap(detail: QuestionnaireDetail): AnswerMap {
  return new Map(detail.answers.map((a) => [a.questionKey, a.value]));
}

interface ItemEntry {
  itemIndex: number;
  fields: ItemFieldMap;
}

// Grouped and sorted by wire itemIndex, not array position -- gaps in the
// sequence are normal once an item has been removed (see apps/api's
// deleteAnswerItem), so item_index is never rendered as a count.
function toItemEntriesByQuestion(detail: QuestionnaireDetail): Map<string, ItemEntry[]> {
  const byKey = new Map<string, ItemEntry[]>();
  for (const item of detail.items) {
    const fields: ItemFieldMap = new Map(item.fields.map((f) => [f.fieldKey, f.value]));
    const existing = byKey.get(item.questionKey) ?? [];
    existing.push({ itemIndex: item.itemIndex, fields });
    byKey.set(item.questionKey, existing);
  }
  for (const [key, entries] of byKey) {
    byKey.set(
      key,
      [...entries].sort((a, b) => a.itemIndex - b.itemIndex),
    );
  }
  return byKey;
}

function toItemMap(entriesByQuestion: Map<string, ItemEntry[]>): ItemMap {
  const map = new Map<string, readonly ItemFieldMap[]>();
  for (const [key, entries] of entriesByQuestion) {
    map.set(
      key,
      entries.map((entry) => entry.fields),
    );
  }
  return map;
}

function StatusLine({ detail }: { detail: QuestionnaireDetail }) {
  const { t } = useTranslation("clinic");
  const { tag } = useFormatters();

  const statusLabel: Record<QuestionnaireStatus, string> = {
    not_started: t("screenerPane.status.notStarted", "Not started"),
    in_progress: t("screenerPane.status.inProgress", "In progress"),
    submitted: t("screenerPane.status.submitted", "Submitted"),
  };

  const dateValue = detail.submittedAt ?? detail.lastSavedAt;
  const dateLabel = dateValue
    ? new Intl.DateTimeFormat(tag, { year: "numeric", month: "short", day: "numeric" }).format(new Date(dateValue))
    : null;

  return (
    <dl className={s.dl}>
      <div className={s.row}>
        <dt className={s.dt}>{t("screenerPane.statusLabel", "Status")}</dt>
        <dd className={s.dd}>{statusLabel[detail.status]}</dd>
      </div>
      <div className={s.row}>
        <dt className={s.dt}>
          {detail.submittedAt
            ? t("screenerPane.submittedOn", "Submitted")
            : t("screenerPane.lastSavedOn", "Last saved")}
        </dt>
        <dd className={s.dd}>{dateLabel ?? t("screenerPane.noDate", "—")}</dd>
      </div>
      <div className={s.row}>
        <dt className={s.dt}>{t("screenerPane.consentLabel", "Collection consent")}</dt>
        <dd className={s.dd}>
          {detail.consentGranted
            ? t("screenerPane.consent.onFile", "On file")
            : t("screenerPane.consent.notOnFile", "Not on file")}
        </dd>
      </div>
    </dl>
  );
}

function AnswerRow({ label, value }: { label: string; value: string | null }) {
  const { t } = useTranslation("clinic");
  return (
    <div className={s.row}>
      <dt className={s.dt}>{label}</dt>
      <dd className={s.dd}>
        {value ?? <span className={s.notAnswered}>{t("screenerPane.notAnswered", "Not answered")}</span>}
      </dd>
    </div>
  );
}

function GroupBlock({
  group,
  entries,
  customerId,
}: {
  group: RepeatingQuestionDefinition;
  entries: ItemEntry[];
  customerId: string;
}) {
  const { t } = useTranslation("clinic");
  const copy = useQuestionnaireCopy();
  const answerText = useAnswerText();
  const groupLabel = copy.groupFor(group.key)?.label ?? group.key;

  if (entries.length === 0) {
    return (
      <dl className={s.dl}>
        <AnswerRow label={groupLabel} value={null} />
      </dl>
    );
  }

  return (
    <div className={s.group}>
      <Text as="h3" size="sm" weight="medium" className={s.groupLabel}>
        {groupLabel}
      </Text>
      {entries.map((entry, position) => (
        <fieldset key={entry.itemIndex} className={s.item}>
          <legend className={s.itemLegend}>
            {t("screenerPane.itemNumber", "Item {{number}}", { number: position + 1 })}
          </legend>
          <dl className={s.dl}>
            {answerableGroupFields(group, entry.fields).map((field) => {
              const value = entry.fields.get(field.key);
              const label = copy.groupFieldFor(group.key, field.key)?.label ?? field.key;
              const photoIds =
                field.control === "photo_upload" && value?.kind === "text" ? parsePhotoIds(value.value) : null;
              if (photoIds) {
                return photoIds.length > 0 ? (
                  <div key={field.key} className={s.row}>
                    <dt className={s.dt}>{label}</dt>
                    <dd className={s.dd}>
                      <QuestionnairePhotoViewer customerId={customerId} photoIds={photoIds} />
                    </dd>
                  </div>
                ) : (
                  <AnswerRow key={field.key} label={label} value={null} />
                );
              }
              return (
                <AnswerRow
                  key={field.key}
                  label={label}
                  value={value ? answerText.forGroupField(group.key, field, value) : null}
                />
              );
            })}
          </dl>
        </fieldset>
      ))}
    </div>
  );
}

function SingleQuestionRow({ question, value }: { question: SingleQuestionDefinition; value: AnswerValue | undefined }) {
  const copy = useQuestionnaireCopy();
  const answerText = useAnswerText();
  const label = copy.questionFor(question.key)?.label ?? question.key;
  return <AnswerRow label={label} value={value ? answerText.forQuestion(question, value) : null} />;
}

interface SectionBlockProps {
  customerId: string;
  section: SectionProgress;
  title: string;
  questions: QuestionDefinition[];
  answers: AnswerMap;
  itemEntriesByQuestion: Map<string, ItemEntry[]>;
  expanded: boolean;
  onToggle: () => void;
}

function SectionBlock({ customerId, section, title, questions, answers, itemEntriesByQuestion, expanded, onToggle }: SectionBlockProps) {
  const { t } = useTranslation("clinic");
  const bodyId = useId();

  return (
    <div className={s.section} data-expanded={expanded}>
      <button type="button" className={s.sectionHead} aria-expanded={expanded} aria-controls={bodyId} onClick={onToggle}>
        <span className={s.sectionTitle}>{title}</span>
        <span className={s.sectionMeta}>
          <Text as="span" size="xs" className={s.sectionCount}>
            {t("screenerPane.sectionCount", "{{answered}}/{{reachable}} answered", {
              answered: section.answered,
              reachable: section.reachable,
            })}
          </Text>
          <ChevronDown className={s.chevron} size={16} strokeWidth={2} aria-hidden />
        </span>
      </button>
      <div id={bodyId} className={s.sectionBody} hidden={!expanded}>
        {expanded &&
          questions.map((question) =>
            question.repeating ? (
              <GroupBlock
                key={question.key}
                group={question}
                entries={itemEntriesByQuestion.get(question.key) ?? []}
                customerId={customerId}
              />
            ) : (
              <dl key={question.key} className={s.dl}>
                <SingleQuestionRow question={question} value={answers.get(question.key)} />
              </dl>
            ),
          )}
      </div>
    </div>
  );
}

/* The clinic console's Screener tab: a client's health-questionnaire answers,
 * read exactly as answered.
 *
 * Sections render in INSTRUMENT_DEFINITIONS order and nothing else -- never
 * an order derived from which questions were answered. Per-section counts
 * come from sectionProgress (packages/api-contract/src/questionnaire-engine.ts),
 * the same derivation the client's HubScreen already uses; this pane does
 * not compute a second one. The only other derivation on this pane is
 * answered-vs-not-answered for an individual reachable question -- the one
 * derivation root CLAUDE.md's "the software does not interpret" permits.
 * There is no score, band, flag, colour, ranking or trend anywhere here, and
 * no export, print, download or copy-all affordance -- see
 * ScreenerPane.test.tsx's purity assertions.
 */
export function ScreenerPane({ customerId, staffUserId }: ScreenerPaneProps) {
  const { t } = useTranslation("clinic");
  const tracker = useTracker();
  const state = useCustomerQuestionnaire(customerId);
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(new Set());

  const detail = state.status === "success" ? state.detail : null;

  const answers = useMemo<AnswerMap>(() => (detail ? toAnswerMap(detail) : new Map<string, AnswerValue>()), [detail]);
  const itemEntriesByQuestion = useMemo(
    () => (detail ? toItemEntriesByQuestion(detail) : new Map<string, ItemEntry[]>()),
    [detail],
  );
  const items = useMemo(() => toItemMap(itemEntriesByQuestion), [itemEntriesByQuestion]);

  const sections = useMemo<SectionProgress[]>(
    () => (detail ? sectionProgress(detail.instrument, answers, items) : []),
    [detail, answers, items],
  );
  const reachableKeys = useMemo(() => {
    const set = new Set<string>();
    if (detail) {
      for (const question of reachableQuestions(detail.instrument, answers)) set.add(question.key);
    }
    return set;
  }, [detail, answers]);
  const questionsByKey = useMemo(() => {
    const map = new Map<string, QuestionDefinition>();
    if (detail) {
      for (const question of INSTRUMENT_DEFINITIONS[detail.instrument].questions) map.set(question.key, question);
    }
    return map;
  }, [detail]);

  const copy = useQuestionnaireCopy();

  function toggle(sectionId: string) {
    const opening = !expandedIds.has(sectionId);
    const next = new Set(expandedIds);
    if (opening) {
      next.add(sectionId);
    } else {
      next.delete(sectionId);
    }
    setExpandedIds(next);
    if (opening && staffUserId) {
      tracker.track("clinic.patient_questionnaire_section_opened", {
        viewed_by: staffUserId,
        patient_id: customerId,
        section_id: sectionId,
      });
    }
  }

  if (state.status === "loading") {
    return (
      <Text as="p" size="sm" className={s.helper}>
        {t("screenerPane.loading", "Loading questionnaire…")}
      </Text>
    );
  }

  if (state.status === "error") {
    return (
      <Text as="p" size="sm" className={s.helper}>
        {t("screenerPane.error", "Couldn't load the questionnaire.")}
      </Text>
    );
  }

  if (!detail || detail.status === "not_started") {
    return (
      <div className={s.emptyBox}>
        <Text as="p" size="sm" weight="medium">
          {t("screenerPane.empty.title", "Not started")}
        </Text>
        <Text as="p" size="sm">
          {t("screenerPane.empty.body", "This customer hasn't started the health questionnaire yet.")}
        </Text>
      </div>
    );
  }

  const instrumentDefinition = INSTRUMENT_DEFINITIONS[detail.instrument];

  return (
    <div className={s.root}>
      <StatusLine detail={detail} />
      {sections.map((section) => {
        const definitionSection = instrumentDefinition.sections.find((sec) => sec.id === section.sectionId);
        const questions = (definitionSection?.questionKeys ?? [])
          .map((key) => questionsByKey.get(key))
          .filter((q): q is QuestionDefinition => q !== undefined && reachableKeys.has(q.key));
        return (
          <SectionBlock
            customerId={customerId}
            key={section.sectionId}
            section={section}
            title={copy.sectionFor(section.sectionId)?.title ?? section.sectionId}
            questions={questions}
            answers={answers}
            itemEntriesByQuestion={itemEntriesByQuestion}
            expanded={expandedIds.has(section.sectionId)}
            onToggle={() => toggle(section.sectionId)}
          />
        );
      })}
    </div>
  );
}
